import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { BiContainer, ConfirmationModal, Divider, Row, TextButton } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { getTimeActivitiesServiceOptions } from '~/business-logic/billServiceSelector';
import BillChargeTableItem from '~/components/BillClientModal/BillChargeTableItem/BillChargeTableItem';
import { BillingType } from '~/components/BillClientModal/BillClientModal.types';
import BillingDate from '~/components/BillClientModal/BillingDate/BillingDate';
import { getCtaText, normalizeEffectiveDate } from '~/components/BillClientModal/BillingDate/BillingDate.utils';
import BillServiceTableItem from '~/components/BillClientModal/BillServiceTableItem/BillServiceTableItem';
import { billingStagesTextsPath, createEmptyChargeItem, createEmptyServiceItem, createServiceItemWithStages, getStageNote } from '~/components/BillClientModal/v2/shared/Lines/lineItem.utils';
import { useStore, useStoreActions } from '~/contexts';
import { useQboTimeActivities } from '~/hooks/reactQuery/queries/useQboTimeActivities';
import { useNonNullableRelationship } from '~/hooks/reactQuery/queries/useRelationship';
import { useAttachFiles } from '~/hooks/useAttachFiles';
import { useLocaleKeys } from '~/i18n';
import { relationshipModel, TimeActivitiesModel } from '~/models';
import { costDisplayTextsPath } from '~/models/service-cost/selectors';
import { BIRelationshipScopeProvider } from '~/providers/BIRelationshipScopeProvider';
import { selectors } from '~/store/state-selectors';
import { billingPeriodTextsPath } from '~/utils/billing-period-text';
import AttachFiles from '../AttachFiles/AttachFiles';
import { Attachments } from '../Attachments/Attachments';
import AddNewItemButtons from './AddNewItemButtons/AddNewItemButtons';
import styles from './BillClientModal.module.scss';
import { BillClientTableAction } from './BillClientTableAction';
import { CreditsWarningCallout, RelationshipEffectiveDateCallout } from './Callouts/Callouts';
import { useAgreementServiceOptions } from './hooks/useAgreementServiceOptions';
import { useBillClientForm } from './hooks/useBillClientForm';
import { useBillClientSubmit } from './hooks/useBillClientSubmit';
import { useInvoiceDateContext } from './hooks/useInvoiceDateContext';
import TimeActivitiesDrawer from './TimeActivitiesDrawer/TimeActivitiesDrawer';
export const GetBillClientModalTID = (parentTestId)=>getTestIds(parentTestId);
function ScopedBillClientModal(props) {
    const { manualServicesBilling, totalCredits, dismissModal, defaultItem, controlData } = props;
    const { BillClientModalTID } = useMemo(()=>GetBillClientModalTID(), []);
    const { relationship, optimisticUpdate } = useNonNullableRelationship(props.relationshipId);
    const dateContext = useInvoiceDateContext({
        invoiceDateInfo: props.invoiceDateInfo,
        earliestBillableDate: props.earliestBillableDate,
        effectiveDate: relationship.effectiveDate
    });
    const { invoiceDateInfo, earliestBillableDate, effectiveDate, now } = dateContext;
    const { t: globalBillTexts } = useLocaleKeys('components.globalBillModal');
    const { t: billTexts } = useLocaleKeys('components.billClientModal');
    const { logBI } = useStoreActions();
    const { t: stagesTexts } = useLocaleKeys(billingStagesTextsPath);
    const { t: costDisplayTexts } = useLocaleKeys(costDisplayTextsPath);
    const { t: billingTexts } = useLocaleKeys(billingPeriodTextsPath);
    const allowTimeActivities = useStore(selectors.integration.allowTimeActivities);
    const [showTimeActivities, setShowTimeActivities] = useState(false);
    const { data: timeActivitiesData } = useQboTimeActivities(relationship.id);
    const timeActivities = timeActivitiesData !== null && timeActivitiesData !== void 0 ? timeActivitiesData : new TimeActivitiesModel();
    const { attachments, openFilePicker, isUploading, handleRemove } = useAttachFiles();
    const timeActivitiesServiceOptions = useMemo(()=>{
        if (!allowTimeActivities) return [];
        const options = getTimeActivitiesServiceOptions({
            services: relationship.services,
            controlData,
            defaultServiceId: defaultItem.type === BillingType.Service ? defaultItem.service.id : undefined,
            costDisplayTexts,
            periodTexts: billingTexts.periods
        });
        return options;
    }, [
        allowTimeActivities,
        controlData,
        defaultItem,
        relationship.services
    ]);
    const _useBillClientForm = useBillClientForm({
        defaultItem: props.defaultItem,
        dateContext
    }), { itemsControl, billModeOptions } = _useBillClientForm, form = _object_without_properties(_useBillClientForm, [
        "itemsControl",
        "billModeOptions"
    ]);
    const [mode, issueDate, items] = form.watch([
        'mode',
        'issueDate',
        'items'
    ]);
    const serviceOptions = useAgreementServiceOptions({
        services: relationship.services,
        controlData,
        items
    });
    const { onSubmit } = useBillClientSubmit({
        form,
        mode,
        relationshipId: relationship.id,
        now,
        dismissModal,
        isUploading,
        attachments,
        timeActivities,
        allowTimeActivities
    });
    const handleCloseModal = ()=>{
        logBI(18404);
        dismissModal();
    };
    const handleDeleteItem = (itemIndex)=>{
        logBI(18405);
        itemsControl.remove(itemIndex);
    };
    const onAddOutOfScopeCharge = ()=>{
        logBI(18407);
        itemsControl.append(createEmptyChargeItem());
    };
    const onAddServiceFromAgreement = (param)=>{
        let { serviceId } = param;
        logBI(18406, {
            relationshipId: relationship.id
        });
        const service = relationship.services.find((s)=>s.id === serviceId);
        if (service) {
            itemsControl.append(createEmptyServiceItem(service));
        } else {
            throw new Error("Service with id ".concat(serviceId, " not found for bill client modal add service button"));
        }
    };
    const onAddServiceInStages = (param)=>{
        let { serviceId, stageNumber, billingStages } = param;
        logBI(18406, {
            relationshipId: relationship.id
        });
        const service = relationship.services.find((s)=>s.id === serviceId);
        if (!service) throw new Error("Service with id ".concat(serviceId, " not found for bill client modal add service button"));
        itemsControl.append(createServiceItemWithStages({
            service,
            stageNumber,
            billingStages,
            note: getStageNote({
                stagesLength: billingStages.length,
                stageNumber,
                t: stagesTexts
            })
        }));
    };
    const renderTableItem = (item, itemIndex)=>{
        switch(item.type){
            case BillingType.Service:
                return /*#__PURE__*/ _jsx(BillServiceTableItem, {
                    serviceItem: item,
                    itemIndex: itemIndex,
                    relationshipEffectiveDate: normalizeEffectiveDate(effectiveDate),
                    manualServiceBilling: manualServicesBilling[item.service.id],
                    actionColumn: /*#__PURE__*/ _jsx(BillClientTableAction, {
                        showDeleteButton: items.length > 1,
                        item: item,
                        itemIndex: itemIndex,
                        timeActivities: timeActivities,
                        allowTimeActivities: allowTimeActivities,
                        onDeleteItem: handleDeleteItem
                    })
                }, item.itemId);
            case BillingType.Charge:
                return /*#__PURE__*/ _jsx(BillChargeTableItem, {
                    itemIndex: itemIndex,
                    chargeItem: item,
                    relationship: relationship,
                    invoiceDateInfo: invoiceDateInfo,
                    actionColumn: /*#__PURE__*/ _jsx(BillClientTableAction, {
                        showDeleteButton: items.length > 1,
                        item: item,
                        itemIndex: itemIndex,
                        timeActivities: timeActivities,
                        allowTimeActivities: allowTimeActivities,
                        onDeleteItem: handleDeleteItem
                    })
                }, item.itemId);
        }
    };
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        title: globalBillTexts.billClientTitle({
            customerName: relationship.customer.name
        }),
        width: "1600px",
        primaryButtonLabel: getCtaText({
            mode,
            ctaTexts: billTexts.ctaLabels
        }),
        primaryButtonDisabled: isUploading,
        testId: BillClientModalTID.Root(),
        textAlignCenter: false,
        onPrimaryButtonClick: form.handleSubmit(onSubmit),
        onCloseButtonClick: handleCloseModal,
        bi: {
            primaryButton: {
                name: 'bill'
            },
            secondaryButton: {
                name: 'cancel'
            },
            closeButton: {
                name: 'close'
            }
        },
        body: !!timeActivitiesServiceOptions.length && /*#__PURE__*/ _jsx(TimeActivitiesDrawer, {
            open: showTimeActivities,
            relationship: relationship,
            itemsControl: itemsControl,
            serviceOptions: timeActivitiesServiceOptions,
            timeActivities: timeActivities,
            close: ()=>setShowTimeActivities(false),
            getValues: form.getValues,
            setRelationship: optimisticUpdate
        }),
        content: /*#__PURE__*/ _jsx(FormProvider, _object_spread_props(_object_spread({}, form), {
            children: /*#__PURE__*/ _jsx(BiContainer, {
                name: "bill_client",
                data: {
                    relationshipId: relationship.id,
                    clientId: relationship.customer.id
                },
                children: /*#__PURE__*/ _jsx("div", {
                    className: styles.ensureNoHorizontalScrollFromDrawer,
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: styles.modalContentWrapper,
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: styles.billModalDatesWrapper,
                                children: [
                                    /*#__PURE__*/ _jsx(BillingDate, {
                                        earliestBillableDate: earliestBillableDate,
                                        billModeOptions: billModeOptions,
                                        invoiceDateInfo: invoiceDateInfo,
                                        effectiveDate: effectiveDate
                                    }),
                                    /*#__PURE__*/ _jsxs(Row, {
                                        gap: 16,
                                        alignItems: "center",
                                        children: [
                                            /*#__PURE__*/ _jsx(AttachFiles, {
                                                attachments: attachments,
                                                openFilePicker: openFilePicker,
                                                isUploading: isUploading
                                            }),
                                            allowTimeActivities && /*#__PURE__*/ _jsx(Divider, {
                                                orientation: "vertical",
                                                margin: 0,
                                                size: 20
                                            }),
                                            allowTimeActivities && /*#__PURE__*/ _jsx(TextButton, {
                                                tooltip: !timeActivitiesServiceOptions.length ? {
                                                    title: billTexts.noActiveUnitBasedServices(),
                                                    placement: 'top'
                                                } : undefined,
                                                disabled: !timeActivitiesServiceOptions.length,
                                                weight: "normal",
                                                className: styles.showTimeActivitiesText,
                                                testId: BillClientModalTID.ShowTimeActivities(),
                                                bi: {
                                                    name: 'show_time_activities'
                                                },
                                                onClick: ()=>setShowTimeActivities(true),
                                                underline: "none",
                                                children: billTexts.showTimeActivities()
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(CreditsWarningCallout, {
                                totalCredits: totalCredits,
                                testId: BillClientModalTID.HasCreditsCallout()
                            }),
                            /*#__PURE__*/ _jsx(RelationshipEffectiveDateCallout, {
                                issueDate: issueDate,
                                effectiveDate: effectiveDate,
                                testId: BillClientModalTID.IssueDateBeforeRelationshipEffectiveDateCallout()
                            }),
                            items.map(renderTableItem),
                            attachments.length > 0 && /*#__PURE__*/ _jsx(Attachments, {
                                files: attachments,
                                onRemove: handleRemove,
                                disableDownloadLinks: true
                            }),
                            /*#__PURE__*/ _jsx(AddNewItemButtons, {
                                serviceOptions: serviceOptions,
                                handleAddOutOfScopeCharge: onAddOutOfScopeCharge,
                                handleAddServiceFromAgreement: onAddServiceFromAgreement,
                                handleAddServiceInStages: onAddServiceInStages
                            })
                        ]
                    })
                })
            })
        }))
    });
}
export default function BillClientModal(props) {
    const { relationship } = useNonNullableRelationship(props.relationshipId);
    const isApproved = relationshipModel.isApproved(relationship);
    return /*#__PURE__*/ _jsx(BIRelationshipScopeProvider, {
        relationshipScope: {
            id: props.relationshipId,
            isApproved
        },
        children: /*#__PURE__*/ _jsx(ScopedBillClientModal, _object_spread({}, props))
    });
}
